import "../../freightDetailPanel/index.css";
import "../../freightDetailPanel/components/CommentModal.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import RailFreightRouteDetails from "../../freightDetailPanel/components/RailFreightRouteDetails";
import RailRouteLocationsDetails from "../../freightDetailPanel/components/RailRouteLocationsDetails";
import QuotedRateHeader from "./QuotedRateHeader";
import CloseIcon from "../../../components/CloseIcon";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import { icons } from "../../../svg";
import NitroRequestService from "../../../utils/nitroRequest.service";
import { useNitroStore } from "../../../store/Store";
import { responseError } from "../../../utils/utils";
import QuotedRateRequestPanel from "../../mobile/railStationLookupPanel/QuotedRateRequestPanel";
import NitroService from "../../../utils/nitro.service";
import {
  isRailFreightQuotedOrNotified,
  getQuoteRowKey,
  findStationForRate,
} from "./multiLocationQuoteUtils";
import { FreightRail } from "../../../types/ICompAnalysisQMD";

const FreightDetailPanel: React.FC = () => {
  const [
    setDisplayFreightDetailsPanel,
    setSelectYourOfferFreight,
    setCloseFreightDetailsPanel,
    railTeamNotifiedRows,
    railFreightDetailContext,
    addRailTeamNotifiedRow,
  ] = useNitroCAStore((state) => [
    state.setDisplayRailFreightDetailsPanel,
    state.setSelectYourOfferFreight,
    state.setCloseRailFreightDetailsPanel,
    state.railTeamNotifiedRows,
    state.railFreightDetailContext,
    state.addRailTeamNotifiedRow,
  ]);
  const [isModalOpen] = useState<boolean>(false);
  const [isNotifyingRailTeam, setIsNotifyingRailTeam] =
    useState<boolean>(false);
  const [showQuotedRateRequestPanel, setShowQuotedRateRequestPanel] =
    useState<boolean>(false);
  const [firstShipDate, setFirstShipDate] = useState<string>("");
  const [carsSold, setCarsSold] = useState<string>("");
  const [quotedRateComments, setQuotedRateComments] = useState<string>("");
  const auth = useAuth();
  const [product, featureFlags] = useNitroStore((state) => [
    state.product,
    state.featureFlags,
  ]);
  const { multiRailStation } = featureFlags;
  const routeDetailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setShowQuotedRateRequestPanel(false);
    setFirstShipDate("");
    setCarsSold("");
    setQuotedRateComments("");
  }, [railFreightDetailContext]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [isModalOpen]);

  const isValidFirstShipDate = useCallback((value: string): boolean => {
    const trimmedValue = value.trim();
    const dateMatch = trimmedValue.match(
      /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(\d{4})$/,
    );

    if (!dateMatch) {
      return false;
    }

    const month = Number(dateMatch[1]);
    const day = Number(dateMatch[2]);
    const year = Number(dateMatch[3]);
    const parsedDate = new Date(year, month - 1, day);

    return (
      parsedDate.getFullYear() === year &&
      parsedDate.getMonth() === month - 1 &&
      parsedDate.getDate() === day
    );
  }, []);

  const isValidCarsSold = useCallback((value: string): boolean => {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return false;
    }

    return /^\d+(\.\d+)?$/.test(trimmedValue);
  }, []);

  const canSubmitQuotedRateRequest =
    isValidFirstShipDate(firstShipDate) && isValidCarsSold(carsSold);

  const handleCloseQuotedRateRequestPanel = useCallback(() => {
    if (isNotifyingRailTeam) {
      return;
    }

    setShowQuotedRateRequestPanel(false);
  }, [isNotifyingRailTeam]);

  const handleSubmitQuotedRateRequest = useCallback(async () => {
    if (!railFreightDetailContext || !canSubmitQuotedRateRequest) {
      return;
    }
    const { quote, destination, rowKey } = railFreightDetailContext;
    const freight = quote.freight as FreightRail;
    setIsNotifyingRailTeam(true);

    try {
      let railTeamUpdated = true;
      try {
        await NitroService.updateRailTeamNotified(freight.id);
      } catch (updateError) {
        railTeamUpdated = false;
        console.error("UpdateRailTeamNotified failed:", updateError);
      }

      await NitroRequestService.postPublishQuotedRailRateRequest({
        marketer: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
        product,
        quotedRate: String(freight.totalFreight),
        originStation: findStationForRate(
          multiRailStation,
          quote.origin.details,
          freight.originStation,
        ),
        destination: {
          city: destination.city || "",
          address: destination.details?.address || "",
          zipCode: destination.details?.zipCode || "",
          station: findStationForRate(
            multiRailStation,
            destination.details,
            freight.destinationStation,
          ),
        },
        firstShipDate: firstShipDate.trim(),
        carsSold: carsSold.trim(),
        comments: quotedRateComments.trim(),
      });

      if (railTeamUpdated) {
        // desktop and mobile build different row keys for the same quote, so register both
        new Set([rowKey, getQuoteRowKey(quote)].filter(Boolean)).forEach(
          addRailTeamNotifiedRow,
        );
      }
      setShowQuotedRateRequestPanel(false);
    } catch (err) {
      responseError(err);
    } finally {
      setIsNotifyingRailTeam(false);
    }
  }, [
    railFreightDetailContext,
    canSubmitQuotedRateRequest,
    auth.user?.profile.given_name,
    auth.user?.profile.family_name,
    product,
    multiRailStation,
    firstShipDate,
    carsSold,
    quotedRateComments,
    addRailTeamNotifiedRow,
  ]);

  if (!railFreightDetailContext) return null;
  const { quote, destination } = railFreightDetailContext;
  const freight = quote.freight as FreightRail;
  const { isQuoted, isNotified } = isRailFreightQuotedOrNotified(
    railTeamNotifiedRows,
    quote,
  );

  return (
    <div className="freight-detail-panel">
      <div className="freight-detail-panel-body">
        <div className="route-freight-details-panel-container">
          {/* Title bar */}
          <div className="freight-detail-title">
            <span className="route-details-title-content">{freight.route}</span>
            <span className="margin-bottom-2 padding-bottom-35">
              <CloseIcon
                onClick={() => {
                  setSelectYourOfferFreight(true);
                  setDisplayFreightDetailsPanel(false);
                  setCloseFreightDetailsPanel(true);
                }}
              />
            </span>
          </div>

          {/* Content area */}
          <div
            id="route-details-content"
            className="route-details-content"
            ref={routeDetailsRef}
          >
            <div className="route-details-inner">
              <div className="top-section">
                <div className=" send-form-white-background">
                  {isQuoted && (
                    <QuotedRateHeader
                      notified={isNotified}
                      isNotifying={isNotifyingRailTeam}
                      onNotifyRailTeam={() =>
                        setShowQuotedRateRequestPanel(true)
                      }
                    />
                  )}
                </div>
                <div className="top-content margin-top-10">
                  <RailRouteLocationsDetails
                    origin={quote.origin}
                    freight={freight}
                    destination={destination}
                  />
                </div>
              </div>

              {/* section separator */}
              <div
                style={{
                  justifyContent: "center",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  data-tooltip-id="section-separator-ircle"
                  src={icons.SectionSeparatorCircle}
                  alt="SectionSeparatorCircle"
                  className="section-separator-circle-left"
                />
                <img
                  data-tooltip-id="section-separator-left"
                  src={icons.SectionSeparatorLeft}
                  alt="SectionSeparatorLeft"
                />
                <div className="circle-container">
                  <img
                    src={icons.TrainWhite}
                    alt="RailIcon"
                    className="rail-icon"
                  />
                </div>

                <img
                  data-tooltip-id="section-separator-right"
                  src={icons.SectionSeparatorRight}
                  alt="SectionSeparatorRight"
                />
                <img
                  data-tooltip-id="section-separator-ircle"
                  src={icons.SectionSeparatorCircle}
                  alt="SectionSeparatorCircle"
                  className="section-separator-circle-right"
                />
              </div>

              <div className="bottom-section">
                <RailFreightRouteDetails freight={freight} />
              </div>
            </div>
          </div>

          {showQuotedRateRequestPanel && (
            <QuotedRateRequestPanel
              firstShipDate={firstShipDate}
              carsSold={carsSold}
              comments={quotedRateComments}
              canSubmit={canSubmitQuotedRateRequest}
              isSubmitting={isNotifyingRailTeam}
              onClose={handleCloseQuotedRateRequestPanel}
              onSubmit={handleSubmitQuotedRateRequest}
              onFirstShipDateChange={setFirstShipDate}
              onCarsSoldChange={setCarsSold}
              onCommentsChange={setQuotedRateComments}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FreightDetailPanel;
